import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/shared/db/database';
import type { LocalTimelog } from '@/shared/db/database';

export type TimelogSyncStatus = LocalTimelog['syncStatus']; // 'pending' | 'synced' | 'failed'

/**
 * Reactive subscription to a single timelog row in Dexie, keyed by `localId`.
 *
 * - When `localId` is `null` or `undefined`, returns `fallback` immediately
 *   without opening any subscription. This is the correct path for remote-only
 *   rows that never carry a draft id.
 * - When `localId` is a real key, opens a SINGLE-ROW `useLiveQuery` so that any
 *   update to that row (e.g. SyncWorker promoting `pending → synced`) re-renders
 *   the consumer with the new status — no modal remount required.
 * - When the live query is still resolving, or when the row genuinely does not
 *   exist in Dexie, returns `fallback`. This keeps the UI stable while Dexie
 *   warms up and also covers the legitimate "I'm a stale remote row" case.
 *
 * Subscribing by primary key avoids the over-render trap of subscribing to the
 * whole `timelogs` table.
 */
export function useTimelogSyncStatus(
  localId: string | null | undefined,
  fallback: TimelogSyncStatus,
): TimelogSyncStatus {
  const live = useLiveQuery(
    () => (localId ? db.timelogs.get(localId) : undefined),
    [localId],
  );

  if (!localId) return fallback;
  // live is `undefined` while the query is in-flight OR when the row doesn't exist.
  if (!live) return fallback;
  // Defensive: spec allows 'failed' status, callers decide how to render it.
  return live.syncStatus ?? fallback;
}
